import { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'

import { SavingCard } from 'pages/account/components/deposit-card/saving-card'
import DepositCard from 'pages/account/components/deposit-card'
import FilterButton from 'pages/account/components/deposit-card/header/filter-button'
import { AddFilterPopup } from 'pages/account/components/add-filter-popup'
import ContentHeader from 'components/ui/account/content/__header'
import { DownloadCSVButton } from 'components/ui/download-csv'

import { useAppSelector } from 'store/hooks'
import { LoginStatuses, selectedAuthStatus } from 'store/reducers/users'
import { EEarningStatuses, IFormattedEarning } from 'store/fetchers/earnings/enum'
import { fetchUserDeposits } from 'store/fetchers/earnings'
import { useHeader } from 'utils/use-header'
import { UTCFormatDate } from 'components/ui/transaction-history/components/transaction/utils'
import DownloadIcon from 'images/icon-download.svg'

import { saveAs } from 'file-saver'

import useOnClickOutside from 'hooks/use-on-click-outside'

import { hasDayPassed } from 'pages/account/loans/utils'
import StatusList from 'pages/account/components/status-list'

import { LinkButton } from 'components/ui/link-button'
import Search from 'components/ui/search'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import { Links } from '../components/actions/enum'

import { addFilterData, FilterDataType, IFilterData, removeFilterData } from '../../../utils/filter-data'

import FilterTooltip from '../../../components/ui/account/filter-tooltip'

import { EarnStatus, filterItems } from './utils'

import styles from './styles.module.scss'
import EmptyIcon from './icons/empty.svg'

const headerCSVRows =
  [
    'Created at',
    'Saving ID',
    'Current balance',
    'Deposit currency',
    'Deposit network',
    'Annual percentage',
    'Total earned',
    'Status',
    'Closed at',
  ].join(',') + '\r\n'

const Savings = () => {
  const { t } = useTranslation()

  const [isLoading, setLoading] = useState(true)
  const [savings, setSavings] = useState<IFormattedEarning[]>([])
  const [statusFilter, setStatusFilter] = useState<EarnStatus[]>([])
  const [selectedFilter, setSelectedFilter] = useState(filterItems.allSavings)

  const [dateSorting, setDateSorting] = useState<number>(2)
  const [accountBalanceSorting, setAccountBalanceSorting] = useState<number>(0)
  const [searchValue, setSearchValue] = useState('')
  const [filterData, setFilterData] = useState<IFilterData[]>([])

  const [filterPopupIsVisible, setFilterPopupIsVisible] = useState(false)

  const [statusesModalOpened, setStatusesModalOpened] = useState(false)

  const authStatus = useAppSelector(selectedAuthStatus)
  const statusesListRef = useRef(null)

  useOnClickOutside(statusesListRef, () => setStatusesModalOpened(false))

  const head = useHeader(t('savings_dashboard'))
  const router = useRouter()

  const searchTextOnChange = (searchValue: string) => {
    setSearchValue(searchValue)
  }

  const filteredSavings = useMemo(() => {
    let result = savings

    result = result.filter((saving) => {
      return !(saving.status === EEarningStatuses.CONFIRMED && hasDayPassed(saving.created_at))
    })

    if (selectedFilter.statuses.length !== 0) {
      result = result.filter((i) => selectedFilter.statuses?.includes(i.status))
    }

    if (searchValue) {
      result = result.filter((saving) => {
        const checkCode = saving.currency_code.toLowerCase().includes(searchValue.trim().toLowerCase())
        const checkNetwork = saving.currency_network.toLowerCase().includes(searchValue.trim().toLowerCase())

        const checkId = saving.earn_id.toLowerCase().includes(searchValue.trim().toLowerCase())

        if (checkCode || checkNetwork || checkId) return saving

        return null
      })
    }

    if (dateSorting === 1) {
      result = result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
    }

    if (dateSorting === 2) {
      result = result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    }

    if (accountBalanceSorting === 1) {
      result = result.sort((a, b) => Number(a.withdraw.available_amount) - Number(b.withdraw.available_amount))
    }

    if (accountBalanceSorting === 2) {
      result = result.sort((a, b) => Number(b.withdraw.available_amount) - Number(a.withdraw.available_amount))
    }

    return result
  }, [savings, selectedFilter, accountBalanceSorting, statusFilter, searchValue, dateSorting])

  const handleStatusSelect = (status: EarnStatus) => {
    setFilterData((prevFilterData) => {
      let filterDataArr = [...prevFilterData]

      if (!statusFilter.includes(status)) {
        const data: IFilterData = {
          type: FilterDataType.STATUS,
          content: t(status.toLowerCase()),
          data: status,
          onClick: () => {
            setStatusFilter([])
            setFilterData((prevData) => prevData.filter((item) => item.type !== FilterDataType.STATUS))
            setSelectedFilter(filterItems.allSavings)
            selectedFilter.statuses = []
          },
          onDestroy: null,
          filters: [status],
          count: 1,
        }

        filterDataArr = addFilterData(filterDataArr, data, t)
      } else {
        const data: IFilterData = {
          type: FilterDataType.STATUS,
          content: t(statusFilter.find((item) => item !== status)?.toLowerCase()),
          data: statusFilter.find((item) => item !== status),
          onClick: () => {
            return null
          },
          onDestroy: null,
          filters: [status],
          count: 1,
        }

        filterDataArr = removeFilterData(filterDataArr, data, t)
      }

      return filterDataArr
    })

    setStatusFilter((prevStatusFilter) => {
      let newStatusFilter = [...prevStatusFilter]

      if (!newStatusFilter.includes(status)) {
        newStatusFilter.push(status)

        switch (status) {
          case EarnStatus.Active:
            if (!selectedFilter.statuses.includes(EEarningStatuses.ACTIVE)) {
              selectedFilter.statuses.push(EEarningStatuses.ACTIVE)
            }
            break
          case EarnStatus.Closed:
            if (!selectedFilter.statuses.includes(EEarningStatuses.CLOSED)) {
              selectedFilter.statuses.push(EEarningStatuses.CLOSED)
            }
            break
          default:
            break
        }
      } else {
        const set = new Set(newStatusFilter)
        set.delete(status)
        newStatusFilter = Array.from(set)

        switch (status) {
          case EarnStatus.Active:
            selectedFilter.statuses = selectedFilter.statuses.filter((status) => status !== EEarningStatuses.ACTIVE)
            break
          case EarnStatus.Closed:
            selectedFilter.statuses = selectedFilter.statuses.filter((status) => status !== EEarningStatuses.CLOSED)
            break
          default:
            break
        }
      }
      return newStatusFilter
    })
  }

  const saveStatusFilterStateToLocal = (statusFilterState) => {
    localStorage.setItem('savings::statusFilter', JSON.stringify(statusFilterState))
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedStatusFilterState = JSON.parse(localStorage?.getItem('savings::statusFilter'))
      if (savedStatusFilterState && Array.isArray(savedStatusFilterState)) {
        savedStatusFilterState.forEach((status) => handleStatusSelect(status))
      }
    }
  }, [])

  useEffect(() => {
    saveStatusFilterStateToLocal(statusFilter)
  }, [statusFilter])

  useEffect(() => {
    async function fetcher() {
      setLoading(true)
      const fetchedSavings = await fetchUserDeposits()

      const sortedSavings = fetchedSavings.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )

      setSavings(sortedSavings)
      setLoading(false)
    }

    authStatus === LoginStatuses.LOGIN && fetcher()
  }, [authStatus])

  const saveCSV = () => {
    const rows = []

    filteredSavings.forEach((data) => {
      const {
        created_at,
        updated_at,
        earn_id,
        currency_code,
        currency_network,
        withdraw,
        percents,
        total_earns,
        status,
      } = data

      const res =
        [
          UTCFormatDate(created_at),
          earn_id,
          Number(withdraw.available_amount).toFixed(15),
          currency_code,
          currency_network,
          percents.annual,
          Number(total_earns).toFixed(15),
          status,
          status === 'ACTIVE' ? '' : UTCFormatDate(updated_at),
        ].join(',') + '\r\n'

      rows.push(res)
    })

    saveAs(new Blob([headerCSVRows, ...rows], { type: 'text/csv;charset=utf-8' }), 'CoinRabbit_Saving_statements.csv')
  }

  useEffect(() => {
    if (
      !isLoading &&
      (!savings.length ||
        !savings.filter((saving) => {
          return !(saving.status === EEarningStatuses.CONFIRMED && hasDayPassed(saving.created_at))
        }).length)
    ) {
      void router.push('/account/savings/add?hide-back=true')
    }
  }, [isLoading, savings])

  return (
    <>
      {head}

      <section className={styles.wrapper}>
        <div className={styles.savings__header}>
          <ContentHeader title={t('savings')} className={styles.savings__heading}>
            <DownloadCSVButton className={styles.downloadBtn} onClick={saveCSV} />
          </ContentHeader>

          <LinkButton title={t('add_new_saving_account')} path={Links.NEW_SAVING} />
        </div>

        <div className={styles.list__header}>
          <div className={styles.searchAndFilters}>
            <Search
              className={styles.search}
              onChange={searchTextOnChange}
              value={searchValue}
              placeholder={t('search_by_asset_saving')}
            />
            <button className={styles.csvBtn} onClick={saveCSV}>
              <img src={DownloadIcon} className={styles.downloadIcon} alt="icon download" />
              <p onClick={saveCSV}>{t('download_csv')}</p>
            </button>
            <FilterButton onClick={() => setFilterPopupIsVisible(true)} className={styles.filterIcon} isActive={true} />
            {filterPopupIsVisible && (
              <AddFilterPopup
                onClose={() => setFilterPopupIsVisible(false)}
                statusFilter={{
                  statuses: [EarnStatus.Active, EarnStatus.Closed],
                  selectedStatuses: statusFilter,
                  onSelect: handleStatusSelect,
                }}
                sortings={[
                  { label: t('account_balance'), state: accountBalanceSorting, setState: setAccountBalanceSorting },
                  { label: t('date'), state: dateSorting, setState: setDateSorting },
                ]}
              />
            )}
          </div>

          <div
            className={cn(styles.filterTooltips, {
              [styles.filterTooltips_noempty]: new Array(...new Set(filterData)).length !== 0,
            })}
          >
            {new Array(...new Set(filterData)).map((item, index) => (
              <FilterTooltip
                imgSrc={item.imgSrc}
                key={index}
                content={item.content}
                onClose={item.onClick}
                type={item.type}
                isDestroying={item.isDestroying}
              />
            ))}
          </div>

          <DepositCard.ColumnsHeader>
            <DepositCard.ColumnHeaderCell
              title={t('account_balance')}
              onClick={() => {
                setDateSorting(0)
                setAccountBalanceSorting((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={accountBalanceSorting}
            />

            <DepositCard.ColumnHeaderCell
              title={t('date')}
              onClick={() => {
                setAccountBalanceSorting(0)
                setDateSorting((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={dateSorting}
            />

            <DepositCard.ColumnHeaderCell title={t('annual_reward')} />
            <DepositCard.ColumnHeaderCell title={t('total_earned_capital')} />

            <div ref={statusesListRef} className={styles.statusList}>
              <DepositCard.ColumnHeaderCell
                title={t('status')}
                onClick={() => setStatusesModalOpened(!statusesModalOpened)}
                isActive={statusFilter.length > 0}
                className={styles.last}
                onClearSort={() => {
                  setStatusFilter([])
                  setSelectedFilter(filterItems.allSavings)
                  setStatusesModalOpened(false)
                  selectedFilter.statuses = []
                  setFilterData([])
                }}
              />
              {statusesModalOpened && (
                <StatusList
                  statuses={[EarnStatus.Active, EarnStatus.Closed]}
                  selectedStatuses={statusFilter}
                  onSelect={handleStatusSelect}
                />
              )}
            </div>
          </DepositCard.ColumnsHeader>
        </div>

        {!isLoading && !!savings?.length && (
          <>
            <div className={styles.container} style={{ height: filteredSavings.length === 0 ? '100%' : undefined }}>
              <div className={styles.list}>
                {filteredSavings
                  .filter((item) => item.status !== EEarningStatuses.NEW)
                  .map((deposit) => (
                    <SavingCard deposit={deposit} key={`header-deposit-${deposit.earn_id}`} />
                  ))}

                {!isLoading && !filteredSavings?.length && (
                  <div className={styles.empty}>
                    <img src={EmptyIcon} alt="empty icon" />
                    <div className={styles.empty__data}>
                      <p>{t('no_matching_savings')}</p>
                      <button
                        onClick={() => {
                          setAccountBalanceSorting(0)
                          setSelectedFilter(filterItems.allSavings)
                          setStatusFilter([])
                          setFilterData([])
                          setSearchValue('')
                          selectedFilter.statuses = []
                        }}
                        className={styles.clear}
                      >
                        {t('clear_filters')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </>
  )
}

export default Savings
